import Swal from "sweetalert2";
import { ApiService } from "./ApiService.js";
import { ImageProcessor } from "../utils/ImageProcessor.js";

const UPLOAD_ENDPOINT = "apiapp/SubirDocumentacion";

export const AdjuntosService = {



	async seleccionarImagenes(max = 6) {
		return new Promise((resolve) => {
			const input = document.createElement("input");
			input.type = "file";
			input.accept = "image/*";
			input.multiple = true;

			input.onchange = () => {
				const files = Array.from(input.files || []);
				resolve(files.slice(0, max));
			};

			input.click();
		});
	},

	/**
	 * Recibe un array de File y retorna array de "nombres" devueltos por la API
	 */
	async subirAdjuntos(files, token, { showLoading = true } = {}) {
		if (!files || files.length === 0) return [];

		if (showLoading) {
			Swal.fire({
				title: "Subiendo imágenes...",
				text: "Por favor esperá",
				allowOutsideClick: false,
				didOpen: () => Swal.showLoading(),
			});
		}


		try {
			const nombres = [];

			for (const file of files) {
				// optimizar 
				const optimizedFile = await ImageProcessor.process(file, {
					quality: 0.7,
					maxWidth: 1024,
					maxHeight: 1024,
					mimeType: "image/webp",
				});

				// 2) subir a endpoint 
				const form = new FormData();
				form.append("file", optimizedFile);

				const resp = await ApiService.postFormData(UPLOAD_ENDPOINT, form, token);

				// Ajustá según tu respuesta real:
				// ejemplo: resp.status==1 y resp.url="archivo.jpg"
				// o resp.message.Nombre, etc.
				if (!resp || resp.status !== 1) {
					throw new Error("Falló la subida de un adjunto");
				}

				const nombre = resp.url || resp.message;
				if (nombre) nombres.push(nombre);
			}

			return nombres;

		} finally {
			if (showLoading) Swal.close();
		}
	},

	buildAdjuntosQuery(nombres = []) {
		if (!nombres || nombres.length === 0) return "";
		return nombres.join("?");
	},

	// agrega documentacion jdoc de los parametros
	/**
	 * 
	 * @param {Object} options
	 * @param {string} options.token Token de autenticación
	 * @param {number} [options.max=6] Máximo de adjuntos permitidos
	 * @param {boolean} [options.showEnviarSinAdjuntos=true] Mostrar botón para enviar sin adjuntos
	 * @param {number} [options.minimoRequerido=0] Mínimo de adjuntos requeridos para confirmar
	 * @returns {Promise<string[]|null>} Array de URLs de adjuntos subidos, o null si se canceló
	 */
	async openUploaderModal({ token, max = 6, showEnviarSinAdjuntos = true, minimoRequerido = 0 } = {}) {
		const uploadedItems = [];

		const renderList = () => {
			const list = document.getElementById("adjuntosList");
			if (!list) return;

			if (uploadedItems.length === 0) {
				list.innerHTML = `<div class="text-muted small">Todavía no agregaste archivos.</div>`;
				return;
			}

			list.innerHTML = uploadedItems
				.map((item, i) => `
      <div class="d-flex align-items-center justify-content-between border rounded p-2 mb-2 gap-2">
        
        <div class="d-flex align-items-center gap-2">
          ${item.isImage
						? `<img src="${item.url}" 
                     alt="adjunto" 
                     style="width:42px;height:42px;object-fit:cover;border-radius:8px;border:1px solid #ddd;" />`
						: `<div style="width:42px;height:42px;display:flex;align-items:center;justify-content:center;border-radius:8px;border:1px solid #ddd;">
                   📎
                 </div>`
					}

          <div class="d-flex flex-column">
            <div class="small fw-bold text-truncate" style="max-width: 260px;">
              ${item.isImage ? "Imagen" : "Archivo"}
            </div>
          </div>
        </div>

        <button class="btn btn-sm btn-outline-danger" data-remove="${i}">
          Quitar
        </button>

      </div>
    `)
				.join("");

			list.querySelectorAll("[data-remove]").forEach(btn => {
				btn.addEventListener("click", () => {
					const idx = Number(btn.getAttribute("data-remove"));
					uploadedItems.splice(idx, 1);
					renderList();
					updateCounter();
				});
			});
		};

		const isImageUrl = (value = "") => {
			const v = (value || "").toLowerCase();
			return v.endsWith(".jpg") || v.endsWith(".jpeg") || v.endsWith(".png") || v.endsWith(".webp") || v.endsWith(".gif");
		};

		const getFileNameFromUrl = (value = "") => {
			try {
				return value.split("/").pop() || value;
			} catch {
				return value;
			}
		};

		const updateCounter = () => {
			const counter = document.getElementById("adjuntosCounter");
			if (!counter) return;
			counter.innerText = `${uploadedItems.length}/${max}`;
		};

		const pickFiles = async () => {
			return new Promise((resolve) => {
				const input = document.createElement("input");
				input.type = "file";
				input.accept = "image/*";
				input.multiple = true;

				input.onchange = () => {
					const files = Array.from(input.files || []);
					resolve(files);
				};

				input.click();
			});
		};

		const result = await Swal.fire({
			title: "Adjuntar imágenes",
			width: 700,
			showConfirmButton: true,
			confirmButtonText: "Enviar",
			showDenyButton: showEnviarSinAdjuntos,
			denyButtonText: "Enviar sin adjuntos",
			showCancelButton: true,
			cancelButtonText: "Cancelar",
			allowOutsideClick: false,
			html: `
        <div class="text-start">
          <div class="d-flex align-items-center justify-content-between mb-2">
            <div>
              <div class="fw-bold">Imágenes</div>
              <div class="text-muted small">Podés subir hasta ${max} fotos.</div>
            </div>
            <div class="badge bg-secondary" id="adjuntosCounter">0/${max}</div>
          </div>

          <div class="d-flex gap-2 mb-3">
            <button id="btnAddPhoto" class="btn btn-primary btn-sm">
              + Agregar fotos
            </button>
            <button id="btnClearAll" class="btn btn-outline-secondary btn-sm">
              Limpiar todo
            </button>
          </div>

          <div id="adjuntosList" style="max-height: 240px; overflow:auto;"></div>

          <div class="mt-3 small text-muted">
            Podés agregar de a una o varias, y se van subiendo automáticamente.
          </div>
        </div>
      `,
			didOpen: () => {
				renderList();
				updateCounter();

				const btnAdd = document.getElementById("btnAddPhoto");
				const btnClear = document.getElementById("btnClearAll");

				btnAdd.addEventListener("click", async () => {
					Swal.resetValidationMessage();
					try {
						if (uploadedItems.length >= max) {
							await Swal.fire("Límite alcanzado", `Máximo ${max} imágenes.`, "warning");
							return;
						}

						const files = await pickFiles();
						if (!files.length) return;

						const disponibles = max - uploadedItems.length;
						const filesToUpload = files.slice(0, disponibles);

						Swal.showLoading();

						const nombresSubidos = await AdjuntosService.subirAdjuntos(filesToUpload, token, { showLoading: false });

						if (Array.isArray(nombresSubidos) && nombresSubidos.length > 0) {
							for (const url of nombresSubidos) {
								uploadedItems.push({
									url,
									name: getFileNameFromUrl(url),
									isImage: isImageUrl(url),
								});
							}
							renderList();
							updateCounter();
						}

					} catch (err) {
						console.error(err);
						await Swal.fire("Error", "No se pudo subir una imagen.", "error");
					} finally {
						Swal.hideLoading();
					}
				});

				btnClear.addEventListener("click", async () => {
					const r = await Swal.fire({
						title: "¿Quitar todas?",
						icon: "warning",
						showCancelButton: true,
						confirmButtonText: "Sí, limpiar",
						cancelButtonText: "No"
					});

					if (r.isConfirmed) {
						uploadedItems.length = 0;
						renderList();
						updateCounter();
					}
				});
			},
			preConfirm: () => {
				if (uploadedItems.length < minimoRequerido && !showEnviarSinAdjuntos) {
					Swal.showValidationMessage(`Al menos ${minimoRequerido} adjunto(s) requerido(s).`);
				} else {
					return uploadedItems.map(x => x.url);
				}
			}
		});

		// BOTONES:
		// Confirm => enviar presupuesto con adjuntos (aunque esté vacío)
		if (result.isConfirmed) {

			return Array.isArray(result.value) ? result.value : [];
		}

		// Deny => cancelar sin adjuntos (pero sigue el envío)
		if (result.isDenied) {
			return [];
		}

		// Cancel => cerrar (no seguir con nada)
		return null;
	}


};
